import React from 'react';
import { Tooltip } from '@mui/material'; // Or your preferred tooltip

type BotStats = {
  views: number;
  threadsStarted: number;
  messagesSent: number;
};

type GradeInfo = {
  letter: string;
  reason: string;
  summary: string;
};

const getGrade = ({ views, threadsStarted, messagesSent }: BotStats): GradeInfo => {
  if (views === 0) return {
    letter: 'N/A',
    reason: 'No views recorded yet.',
    summary: 'No data available to assess engagement.',
  };

  const conversionRate = threadsStarted / views;
  const engagementRate = messagesSent / (threadsStarted || 1); // prevent /0
  let baseGrade = 'F';

  if (conversionRate >= 0.3) baseGrade = 'A+';
  else if (conversionRate >= 0.2) baseGrade = 'B';
  else if (conversionRate >= 0.1) baseGrade = 'C';
  else if (conversionRate >= 0.05) baseGrade = 'D';

  let bonusApplied = false;
  if (engagementRate > 3) {
    bonusApplied = true;
    if (baseGrade === 'B') baseGrade = 'A';
    else if (baseGrade === 'C') baseGrade = 'B';
    else if (baseGrade === 'D') baseGrade = 'C';
    else if (baseGrade === 'F') baseGrade = 'D';
  }

  const reason = `Conversion rate: ${(conversionRate * 100).toFixed(1)}%, Engagement rate: ${engagementRate.toFixed(1)} msgs/thread${bonusApplied ? ' — Bonus applied' : ''}`;

  const summary = `This bot has a ${baseGrade} grade because ${threadsStarted} out of ${views} visitors started a conversation, and users sent an average of ${engagementRate.toFixed(1)} messages per thread.${bonusApplied ? ' High engagement improved the grade.' : ''}`;

  return { letter: baseGrade, reason, summary };
};

const BotEngagementGrade: React.FC<{ stats: BotStats }> = ({ stats }) => {
  const { letter, reason, summary } = getGrade(stats);
  const PURPLE = 'var(--primary)';

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      fontFamily: 'Montserrat',
      padding: '20px',
      background: 'var(--tint)',
      border: `2px solid ${PURPLE}`,
      borderRadius: '16px',
      width: '100%',
      height: '100%',
      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
      gap: '32px',
    }}>
      {/* Left: Grade block */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ fontSize: '14px', color: '#444', marginBottom: 4 }}>
          Bot Engagement Grade
        </div>
        <Tooltip title={reason} arrow>
          <div style={{
            fontSize: '98px',
            fontWeight: 800,
            color: PURPLE,
            lineHeight: '1.2',
            cursor: 'pointer',
          }}>
            {letter}
          </div>
        </Tooltip>
        <div style={{ fontSize: '13px', color: '#555', marginTop: 6 }}>
          {stats.views} views · {stats.threadsStarted} threads · {stats.messagesSent} messages
        </div>
      </div>

      {/* Right: Explanation block */}
      <div style={{
        flex: 1,
        fontSize: '14px',
        color: '#333',
        lineHeight: '1.5',
      }}>
        <strong>What this means:</strong>
        <p style={{ marginTop: 6 }}>{summary}</p>
      </div>
    </div>
  );
};

export default BotEngagementGrade;
